import { useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PartCatalogSummaryDto, PartCategory } from '@bhops/shared';
import { canEditMfg } from '@/permissions';
import { partsApi } from '@/api/parts';
import { extractApiError } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/Form';
import { Icon } from '@/components/Icon';
import { useDebounce } from '@/hooks/useDebounce';
import { PartCatalogCreateModal } from './PartCatalogCreateModal';

// docs/plan/v0.4.0-parts-solution-defect-lot/plan.md §6.1 — 부품 카탈로그 관리.
// SKU 등록 / 편집 — 계획서(2026-08-20) 요구 1 부터 제조팀(MFG_MANAGER) 포함
// (canEditMfg 로 게이트). 일반 사용자는 read-only.

const CATEGORY_LABEL: Record<PartCategory, string> = {
  GRILLER: '그릴러',
  LOADER: '로더',
  GAS_VALVE: '가스 밸브',
  FIRMWARE_BOARD: '펌웨어 보드',
  SENSOR_SUITE: '센서 모듈',
  POWER_UNIT: '전원 유닛',
  CONSUMABLE: '소모품',
  ACCESSORY: '액세서리',
};

export function PartCatalogPage() {
  const role = useAuthStore((s) => s.user?.role);
  // 계획서(2026-08-20) 요구1 ① — 서버 parts.controller 의 카탈로그 등록·수정
  // @Roles 가 MFG_MANAGER 에 열렸다. canEditMfg 가 같은 집합의 화면 정본.
  const canCreate = role != null && canEditMfg(role);
  const [categoryFilter, setCategoryFilter] = useState<PartCategory | ''>('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);
  const [createOpen, setCreateOpen] = useState(false);

  const q = useQuery({
    queryKey: [
      'parts',
      'catalog',
      {
        category: categoryFilter || undefined,
        active: activeOnly,
        search: debouncedSearch || undefined,
        lowStockOnly,
      },
    ],
    queryFn: () =>
      partsApi.listCatalog({
        category: categoryFilter || undefined,
        active: activeOnly,
        search: debouncedSearch || undefined,
        lowStockOnly: lowStockOnly || undefined,
      }),
    staleTime: 30_000,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header
        style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}
      >
        <div>
          <h1 className="kuun-h2" style={{ margin: 0 }}>
            부품 카탈로그
          </h1>
        </div>
        {canCreate && (
          <Button
            type="button"
            onClick={() => setCreateOpen(true)}
            data-testid="catalog-create-button"
          >
            + 부품 등록
          </Button>
        )}
      </header>

      <PartCatalogCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />

      {/* Filter bar */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          flexWrap: 'wrap',
          padding: '8px 12px',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--r-card)',
        }}
      >
        <input
          placeholder="SKU나 이름으로 검색"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={input}
        />
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value as PartCategory | '')}
          style={input}
        >
          <option value="">전체 카테고리</option>
          {(Object.keys(CATEGORY_LABEL) as PartCategory[]).map((c) => (
            <option key={c} value={c}>
              {CATEGORY_LABEL[c]}
            </option>
          ))}
        </select>
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 12,
            color: 'var(--fg-muted)',
          }}
        >
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
          />
          활성만
        </label>
        {/* v0.5.2 — 부족 카탈로그만 필터 */}
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 12,
            color: lowStockOnly ? 'var(--fg-danger)' : 'var(--fg-muted)',
            fontWeight: lowStockOnly ? 600 : 400,
          }}
          title="최소 재고를 정했고 가용 재고가 그 이하인 부품만 보여 줍니다."
        >
          <input
            type="checkbox"
            checked={lowStockOnly}
            onChange={(e) => setLowStockOnly(e.target.checked)}
            data-testid="low-stock-only-filter"
          />
          부족만
        </label>
        <span style={{ color: 'var(--fg-muted)', fontSize: 12, marginLeft: 'auto' }}>
          {q.isFetching ? '불러오는 중…' : `${q.data?.length ?? 0} 건`}
        </span>
      </div>

      {/* Table */}
      <div
        style={{
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--r-card)',
          background: 'var(--bg-surface)',
          overflow: 'auto',
        }}
      >
        {q.isLoading ? (
          <div style={emptyBlock}>불러오는 중…</div>
        ) : q.error ? (
          <div style={{ ...emptyBlock, color: 'var(--fg-danger)' }}>{extractApiError(q.error)}</div>
        ) : !q.data || q.data.length === 0 ? (
          <div style={emptyBlock}>
            {canCreate
              ? '등록된 카탈로그가 없습니다. 위의 [+ 부품 등록]으로 시작해 주세요.'
              : '등록된 카탈로그가 없습니다.'}
          </div>
        ) : (
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>SKU</th>
                <th style={th}>이름</th>
                <th style={th}>카테고리</th>
                <th style={{ ...th, textAlign: 'right' }}>단가 (₩)</th>
                <th style={{ ...th, textAlign: 'right' }}>보증</th>
                <th style={{ ...th, textAlign: 'right' }}>LOT</th>
                <th style={{ ...th, textAlign: 'right' }}>운영 중</th>
                {/* v0.5.2 — 재고 컬럼 */}
                <th style={{ ...th, textAlign: 'right' }}>재고 / 임계</th>
                <th style={th}>기본 공급사</th>
                <th style={{ ...th, textAlign: 'center' }}>활성</th>
              </tr>
            </thead>
            <tbody>
              {q.data.map((c) => (
                <CatalogRow key={c.id} catalog={c} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function CatalogRow({ catalog: c }: { catalog: PartCatalogSummaryDto }) {
  // v0.5.2 — 재고 표시 색.
  const inv = c.inventory;
  let stockColor = 'var(--fg-default)';
  let stockBg: string | undefined;
  if (c.minStockLevel != null) {
    if (inv.usableRemaining === 0) {
      stockColor = 'var(--fg-danger)';
      stockBg = 'var(--bg-danger-subtle)';
    } else if (inv.isLowStock) {
      stockColor = 'var(--severity-warning)';
      stockBg = 'var(--bg-warning-subtle)';
    } else {
      stockColor = 'var(--green-700)';
    }
  } else {
    stockColor = 'var(--fg-muted)';
  }

  return (
    <tr style={{ borderTop: '1px solid var(--border-subtle)', opacity: c.active ? 1 : 0.55 }}>
      <td style={{ ...tdMono, fontWeight: 600 }}>{c.sku}</td>
      <td style={td}>{c.displayName}</td>
      <td style={td}>{CATEGORY_LABEL[c.category]}</td>
      <td style={{ ...tdMono, textAlign: 'right' }}>
        {c.unitPriceKrw != null ? `₩${c.unitPriceKrw.toLocaleString('ko-KR')}` : '–'}
      </td>
      <td style={{ ...tdMono, textAlign: 'right' }}>
        {c.warrantyMonths != null ? `${c.warrantyMonths}개월` : '–'}
      </td>
      <td style={{ ...tdMono, textAlign: 'right' }}>{c.lotCount}</td>
      <td style={{ ...tdMono, textAlign: 'right', fontWeight: 600 }}>{c.inUseCount}</td>
      {/* v0.5.2 — 재고 / 임계 */}
      <td
        style={{
          ...tdMono,
          textAlign: 'right',
          color: stockColor,
          background: stockBg,
          fontWeight: inv.isLowStock ? 700 : 400,
        }}
        title={
          c.minStockLevel == null
            ? '최소 재고를 정하지 않아 재고를 추적하지 않습니다.'
            : `가용 ${inv.usableRemaining}, 총 ${inv.totalRemaining}, 활성 LOT ${inv.activeLotCount}`
        }
      >
        {c.minStockLevel != null ? (
          <>
            {inv.isLowStock && <Icon name="alert" size={12} aria-hidden />}
            {inv.usableRemaining} / {c.minStockLevel}
            {inv.shortage != null && inv.shortage > 0 && (
              <span style={{ marginLeft: 4, fontSize: 10, opacity: 0.8 }}>(-{inv.shortage})</span>
            )}
          </>
        ) : (
          <span style={{ fontStyle: 'italic' }}>미추적</span>
        )}
      </td>
      <td style={{ ...td, fontSize: 11, color: 'var(--fg-muted)' }}>
        {c.defaultSupplier ? (
          <span title={c.defaultSupplier.displayName}>{c.defaultSupplier.code}</span>
        ) : (
          '–'
        )}
      </td>
      {/* 표의 예/아니오 통일 규약 — check = '예', em dash = '해당 없음'. ✕ 는 쓰지 않는다
          (경고/삭제와 그림이 겹친다). docs/design/icon-vocabulary.md 참조. */}
      <td style={{ ...td, textAlign: 'center' }}>
        {c.active ? <Icon name="check" size={14} aria-hidden /> : '—'}
      </td>
    </tr>
  );
}

const input: CSSProperties = {
  height: 30,
  padding: '0 10px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-input)',
  background: 'var(--bg-surface)',
  fontSize: 13,
  color: 'var(--fg-default)',
  minWidth: 140,
};
const table: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th: CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  fontWeight: 600,
  color: 'var(--fg-muted)',
  background: 'var(--bg-sunken)',
  borderBottom: '1px solid var(--border-subtle)',
};
const td: CSSProperties = { padding: '10px 12px', verticalAlign: 'middle' };
const tdMono: CSSProperties = { ...td, fontFamily: 'var(--font-mono)' };
const emptyBlock: CSSProperties = {
  padding: 40,
  textAlign: 'center',
  color: 'var(--fg-muted)',
  fontSize: 13,
};
